import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    IconSettings, 
    Button, 
    DataTable, 
    DataTableColumn, 
    DataTableCell,
    Tabs,
    TabsPanel,
    Alert,
    Toast,
    Icon,
    Input,
    Card,
    Dropdown
} from '@salesforce/design-system-react';
import DataTableRowActions from '@salesforce/design-system-react/components/data-table/row-actions';
// @ts-ignore - DataTableInteractiveLink exists but lacks type definitions
import DataTableInteractiveLink from '@salesforce/design-system-react/lib/components/data-table/interactive-link';
import { IOrgPortalEntry } from 'interfaces/IOrg.interface';
import { normalizeTagNames } from 'common/tag.utils';
import { IPushRequest } from 'interfaces/IPushRequest.interface';
import { PushPortalServiceProvider } from 'services/push-portal.service';
import { SFConnection } from 'common/sf-connector';
import CQSpinner from 'components/cq-spinner';
import OrgRow from 'components/cq-org-row';
import CQConfirmationModal from 'components/cq-confirmation-modal';
import CQManagedPackageInterstitial from 'components/cq-managed-package-interstitial';
import { CQAppConstant } from 'app-constants';
import { getMinSelectableUtcDate, buildScheduledTimeIso, formatDateOnlyInput, formatScheduledDateDisplay } from 'utils/push-portal-scheduling.utils';

/**
 * Custom cell component for Actions column with View link
 * Following the official Salesforce Design System React pattern for interactive elements in DataTable
 */
const CustomActionsCell = ({ item, onView, ...props }: any) => {
    const { t } = useTranslation();
    return (
        <DataTableCell {...props}>
            <DataTableInteractiveLink onClick={() => item && onView(item)}>
                {t('push_portal_view_orgs')}
            </DataTableInteractiveLink>
        </DataTableCell>
    );
};
CustomActionsCell.displayName = DataTableCell.displayName;

/**
 * Type for org type filter values.
 */
type OrgTypeFilter =
    | typeof CQAppConstant.FILTER_MODE.ALL
    | typeof CQAppConstant.ORG_TYPE.PRODUCTION
    | typeof CQAppConstant.ORG_TYPE.SANDBOX;

type ActivityTab =
    | typeof CQAppConstant.ACTIVITY_TAB.PAUSED
    | typeof CQAppConstant.ACTIVITY_TAB.PENDING
    | typeof CQAppConstant.ACTIVITY_TAB.FAILED
    | typeof CQAppConstant.ACTIVITY_TAB.SCHEDULED;

type ScheduleResult = {
    orgId: string;
    success?: boolean;
    error?: string;
};

type ActivityState = {
    items: any[];
    hasPrev: boolean;
    hasNext: boolean;
    nextCursor?: string;
    cursorHistory: string[];
};

/**
 * Create the default empty state shape for an activity tab.
 *
 * Used for initial state and as a fallback when an activity load fails.
 */
function createEmptyActivityState(): ActivityState {
    return {
        items: [],
        hasPrev: false,
        hasNext: false,
        nextCursor: undefined,
        cursorHistory: [],
    };
}

/**
 * Props for the CQ Push Portal UI.
 *
 * This component is rendered inside the management UI and relies on backend APIs
 * exposed via `PushPortalServiceProvider`.
 */
interface CQPushPortalProps {
    /**
     * Whether the user can schedule upgrades/updates.
     */
    canSchedule: boolean;
    
    /**
     * Whether the user can register orgs.
     */
    canRegister: boolean;
    /**
     * Whether the registration modal is open.
     */
    isRegisterOpen: boolean;
    /**
     * Callback to toggle the registration modal.
     */
    setIsRegisterOpen: (value: boolean) => void;
    /**
     * OAuth login URL.
     */
    oauthLoginUrl: string;
    /**
     * Callback to update the OAuth login URL.
     */
    setOauthLoginUrl: (value: string) => void;
    /**
     * Whether the registration is loading.
     */
    regLoading: boolean;
    /**
     * Registration error message.
     */
    regError: string;
    /**
     * Current username.
     */
    currentUsername: string;
    /**
     * Current user email.
     */
    currentUserEmail: string;
    /**
     * Tag names entered in registration modal.
     */
    registerTagNames: string;
    /**
     * Callback to update tag names in registration modal.
     */
    setRegisterTagNames: (value: string) => void;
    /**
     * Tag validation error for registration modal.
     */
    registerTagError: string;
    /**
     * Callback to handle connecting an org.
     */
    handleConnectOrg: () => Promise<void>;
    /**
     * Whether to show the package interstitial inside the modal.
     */
    showPackageInterstitial: boolean;
    /**
     * Callback when "Install Now" is clicked in the interstitial.
     */
    handlePackageInstallNow: () => void;
    /**
     * Callback when "Already Installed - Continue" is clicked in the interstitial.
     */
    handlePackageContinue: () => Promise<void>;
    /**
     * Callback when "Back" is clicked in the interstitial.
     */
    handlePackageBack: () => void;
}

/**
 * CQ Push Portal screen.
 *
 * Renders org list, filter controls, and upgrade activity tabs while
 * coordinating data loads via PushPortalServiceProvider.
 */
function CQPushPortal({ 
    canSchedule,
    canRegister,
    isRegisterOpen,
    setIsRegisterOpen,
    oauthLoginUrl,
    setOauthLoginUrl,
    regLoading,
    regError,
    currentUsername,
    currentUserEmail,
    registerTagNames,
    setRegisterTagNames,
    registerTagError,
    handleConnectOrg,
    showPackageInterstitial,
    handlePackageInstallNow,
    handlePackageContinue,
    handlePackageBack
}: CQPushPortalProps) {
    const { t } = useTranslation();
    const [orgEntries, setOrgEntries] = useState<IOrgPortalEntry[]>([]);
    const [orgPage, setOrgPage] = useState<number>(1);
    const [orgTotalPages, setOrgTotalPages] = useState<number>(1);
    const [orgTotal, setOrgTotal] = useState<number>(0);
    const [orgHasPrev, setOrgHasPrev] = useState<boolean>(false);
    const [orgHasNext, setOrgHasNext] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filterMode, setFilterMode] = useState<typeof CQAppConstant.FILTER_MODE.UPDATE | typeof CQAppConstant.FILTER_MODE.UPGRADE>(CQAppConstant.FILTER_MODE.UPDATE);
    const [filterVersion, setFilterVersion] = useState('');
    const [orgTypeFilter, setOrgTypeFilter] = useState<OrgTypeFilter>(CQAppConstant.FILTER_MODE.ALL);
    const [orgSearch, setOrgSearch] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [versionFilters, setVersionFilters] = useState<{
        upgradeVersions: string[];
        updateVersions: string[];
        updateModulesByVersion: Record<string, any>;
        upgradeModulesByVersionName: Record<string, any>;
    } | null>(null);
    const [selectedOrgs, setSelectedOrgs] = useState<Map<string, { orgName: string; orgType: string }>>(new Map());
    const [orgNamesById, setOrgNamesById] = useState<Record<string, string>>({});
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduledDateTime, setScheduledDateTime] = useState<string>('');
    const [scheduleError, setScheduleError] = useState('');
    const [scheduleResults, setScheduleResults] = useState<{
        error?: string;
        results?: ScheduleResult[];
    } | null>(null);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [selectAllLoading, setSelectAllLoading] = useState(false);
    const [upgradeSummaryLoading, setUpgradeSummaryLoading] = useState(false);
    const [scheduledActivity, setScheduledActivity] = useState<ActivityState>(createEmptyActivityState());
    const [pausedActivity, setPausedActivity] = useState<ActivityState>(createEmptyActivityState());
    const [pendingActivity, setPendingActivity] = useState<ActivityState>(createEmptyActivityState());
    const [failedActivity, setFailedActivity] = useState<ActivityState>(createEmptyActivityState());
    const [summaryTab, setSummaryTab] = useState<ActivityTab>(CQAppConstant.ACTIVITY_TAB.PENDING);

    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState<typeof CQAppConstant.ALERT_VARIANT.SUCCESS | typeof CQAppConstant.ALERT_VARIANT.ERROR | typeof CQAppConstant.ALERT_VARIANT.WARNING>(CQAppConstant.ALERT_VARIANT.SUCCESS);

    const [selectedScheduledRequest, setSelectedScheduledRequest] = useState<IPushRequest | null>(null);
    const [activitySearch, setActivitySearch] = useState<string>('');
    const [abortConfirmOpen, setAbortConfirmOpen] = useState(false);
    const [abortLoading, setAbortLoading] = useState(false);
    const [itemToAbort, setItemToAbort] = useState<any>(null);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [orgIdsToCancel, setOrgIdsToCancel] = useState<string[]>([]);
    const [isCancelAll, setIsCancelAll] = useState(false);
    const [isEditingScheduledTime, setIsEditingScheduledTime] = useState(false);
    const [editScheduledDateTime, setEditScheduledDateTime] = useState('');
    const [editScheduledError, setEditScheduledError] = useState('');
    const [editScheduledConfirmOpen, setEditScheduledConfirmOpen] = useState(false);
    const [editScheduledLoading, setEditScheduledLoading] = useState(false);
    const loadOrgsRequestIdRef = useRef(0);
    const isInitialMount = useRef(true);
    const summaryTabRef = useRef(summaryTab);
    const activitySearchRef = useRef(activitySearch);

    // Keep refs in sync with state for refresh handler
    useEffect(() => {
        summaryTabRef.current = summaryTab;
        activitySearchRef.current = activitySearch;
    }, [summaryTab, activitySearch]);

    useEffect(() => {
        loadOrgs(1);
        loadVersionFilters();
        
        loadActivity(summaryTab, CQAppConstant.PUSH_PORTAL_UI.RESET, true);
        isInitialMount.current = false;
        
        // Expose refresh function to parent component
        (window as any).__pushPortalRefresh = handleRefreshActivity;
        
        // Cleanup on unmount
        return () => {
            delete (window as any).__pushPortalRefresh;
        };
    }, []);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            loadOrgs(1);
        }, CQAppConstant.PUSH_PORTAL_UI.DEBOUNCE_TIMEOUT);
        return () => window.clearTimeout(handle);
    }, [orgSearch]);

    useEffect(() => {
        if (isInitialMount.current) return;
        const handle = window.setTimeout(() => {
            loadActivity(summaryTab, CQAppConstant.PUSH_PORTAL_UI.RESET, false);
        }, CQAppConstant.PUSH_PORTAL_UI.DEBOUNCE_TIMEOUT);
        return () => window.clearTimeout(handle);
    }, [activitySearch]);

    useEffect(() => {
        if (isInitialMount.current) return;
        
        setActivitySearch('');
        loadActivity(summaryTab, CQAppConstant.PUSH_PORTAL_UI.RESET, true, '');
    }, [summaryTab]);

    /**
     * Resolve the stored pagination/data state for a given activity tab.
     *
     * @param tab Activity tab identifier.
     */
    function getActivityState(tab: ActivityTab): ActivityState {
        switch (tab) {
            case CQAppConstant.ACTIVITY_TAB.SCHEDULED:
                return scheduledActivity;
            case CQAppConstant.ACTIVITY_TAB.PAUSED:
                return pausedActivity;
            case CQAppConstant.ACTIVITY_TAB.PENDING:
                return pendingActivity;
            case CQAppConstant.ACTIVITY_TAB.FAILED:
                return failedActivity;
        }
    }

    /**
     * Update the stored pagination/data state for a given activity tab.
     *
     * @param tab Activity tab identifier.
     * @param state Next state snapshot for that tab.
     */
    function setActivityStateForTab(tab: ActivityTab, state: ActivityState) {
        switch (tab) {
            case CQAppConstant.ACTIVITY_TAB.SCHEDULED:
                setScheduledActivity(state);
                break;
            case CQAppConstant.ACTIVITY_TAB.PAUSED:
                setPausedActivity(state);
                break;
            case CQAppConstant.ACTIVITY_TAB.PENDING:
                setPendingActivity(state);
                break;
            case CQAppConstant.ACTIVITY_TAB.FAILED:
                setFailedActivity(state);
                break;
        }
    }

    /**
     * Build the loader/rendering configuration for a given activity tab.
     *
     * Centralizes API loading, item formatting, empty-state text, table IDs,
     * and column rendering so tab behavior stays consistent.
     *
     * @param tab Activity tab identifier.
     */
    function getActivityConfig(tab: ActivityTab) {
        switch (tab) {
            case CQAppConstant.ACTIVITY_TAB.SCHEDULED:
                return {
                    load: (params: any) => PushPortalServiceProvider.listAllScheduledActivity(params),
                    formatItem: (item: any) => ({ ...item, scheduledTimeFormatted: formatScheduledDateDisplay(item.scheduledTime) || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT }),
                    emptyMessage: t('push_portal_no_scheduled_upgrades'),
                    tableId: CQAppConstant.PUSH_PORTAL_UI.IDS.SCHEDULED_ACTIVITY_TABLE,
                    renderColumns: renderScheduledColumns,
                };
            case CQAppConstant.ACTIVITY_TAB.PAUSED:
                return {
                    load: (params: any) => PushPortalServiceProvider.listAllPausedActivity(params),
                    formatItem: (item: any) => ({ ...item, submittedAtFormatted: formatTime(item.submittedAt), updatedAtFormatted: formatTime(item.updatedAt) }),
                    emptyMessage: t('push_portal_no_paused_upgrades'),
                    tableId: CQAppConstant.PUSH_PORTAL_UI.IDS.PAUSED_ACTIVITY_TABLE,
                    renderColumns: () => renderOrgActivityColumns(false),
                };
            case CQAppConstant.ACTIVITY_TAB.PENDING:
                return {
                    load: (params: any) => PushPortalServiceProvider.listAllPendingActivity(params),
                    formatItem: (item: any) => ({ ...item, submittedAtFormatted: formatTime(item.submittedAt), updatedAtFormatted: formatTime(item.updatedAt) }),
                    emptyMessage: t('push_portal_no_pending_upgrades'),
                    tableId: CQAppConstant.PUSH_PORTAL_UI.IDS.PENDING_ACTIVITY_TABLE,
                    renderColumns: () => renderOrgActivityColumns(false, true),
                };
            case CQAppConstant.ACTIVITY_TAB.FAILED:
                return {
                    load: (params: any) => PushPortalServiceProvider.listAllFailedActivity(params),
                    formatItem: (item: any) => ({ ...item, submittedAtFormatted: formatTime(item.submittedAt), updatedAtFormatted: formatTime(item.updatedAt) }),
                    emptyMessage: t('push_portal_no_failed_upgrades'),
                    tableId: CQAppConstant.PUSH_PORTAL_UI.IDS.FAILED_ACTIVITY_TABLE,
                    primaryColumn: 'submittedAtFormatted',
                    renderColumns: () => renderOrgActivityColumns(true),
                };
        }
    }

    /**
     * Load activity for a specific tab with cursor-based pagination.
     * 
     * @param tab Activity tab to load
     * @param direction Navigation direction: 'next', 'prev', or 'reset'
     * @param showLoading Whether to show loading spinner (default: false)
     * @param searchOverride Optional explicit search value (overrides state)
     */
    async function loadActivity(
        tab: ActivityTab,
        direction: 'next' | 'prev' | 'reset' = CQAppConstant.PUSH_PORTAL_UI.RESET,
        showLoading: boolean = false,
        searchOverride?: string
    ) {
        if (showLoading) {
            setUpgradeSummaryLoading(true);
        }
        try {
            const effectiveSearch = searchOverride !== undefined ? searchOverride : activitySearch;

            // Get current state for the tab
            const currentState = getActivityState(tab);

            let cursor: string | undefined;
            let newCursorHistory: string[] = [];

            if (direction === CQAppConstant.PUSH_PORTAL_UI.NEXT) {
                // Use nextCursor to fetch the next page
                cursor = currentState.nextCursor;
                // Store the cursor we're using to reach the next page
                // This allows us to navigate back to this page later
                if (cursor) {
                    newCursorHistory = [...currentState.cursorHistory, cursor];
                } else {
                    newCursorHistory = [...currentState.cursorHistory];
                }
            } else if (direction === CQAppConstant.PUSH_PORTAL_UI.PREV) {
                // Copy current history and remove the last cursor
                newCursorHistory = [...currentState.cursorHistory];
                newCursorHistory.pop();
                // Use the last cursor in the updated history to go back
                cursor = newCursorHistory.length > 0 ? newCursorHistory[newCursorHistory.length - 1] : undefined;
            } else {
                // Reset: start from beginning
                cursor = undefined;
                newCursorHistory = [];
            }

            const { load, formatItem } = getActivityConfig(tab);

            // Lowercase search term for case-insensitive matching
            const searchParam = effectiveSearch ? effectiveSearch : undefined;

            // Fetch paginated data from server with cursor
            const response = await load({
                pageSize: CQAppConstant.PUSH_PORTAL_UI.ACTIVITY_PAGE_SIZE,
                search: searchParam,
                cursor
            });

            // Format timestamps for display
            const formattedItems = response.items.map(formatItem);

            // Update state for the current tab with cursor tracking
            const state = {
                items: formattedItems,
                hasPrev: newCursorHistory.length > 0,
                hasNext: !!response.nextCursor,
                nextCursor: response.nextCursor,
                cursorHistory: newCursorHistory
            };

            setActivityStateForTab(tab, state);
        } catch (err) {
            console.error('Failed to load activity', err);
            setActivityStateForTab(tab, createEmptyActivityState());
        } finally {
            if (showLoading) {
                setUpgradeSummaryLoading(false);
            }
        }
    }

    // Format ISO timestamps for activity tables in user's local timezone.
    function formatTime(value?: string) {
        if (!value) return CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT;
        return date.toLocaleString();
    }

    // Refresh activity data and reload current tab
    function handleRefreshActivity() {
        // Reload current tab from page 1 - show loading spinner
        // Use refs to avoid stale closure and preserve current search state
        loadActivity(
            summaryTabRef.current, 
            CQAppConstant.PUSH_PORTAL_UI.RESET,
            true, 
            activitySearchRef.current
        );
    }

    // Render pager controls for activity tabs.
    function renderPager(tab: ActivityTab) {
        const state = getActivityState(tab);
        return (
            <div className="slds-grid slds-grid_align-end slds-m-top_small">
                <div>
                    <Button label={t('push_portal_prev')} variant="neutral" disabled={!state.hasPrev} onClick={() => loadActivity(tab, CQAppConstant.PUSH_PORTAL_UI.PREV, false)} />
                    <Button label={t('push_portal_next')} variant="neutral" className="slds-m-left_x-small" disabled={!state.hasNext} onClick={() => loadActivity(tab, CQAppConstant.PUSH_PORTAL_UI.NEXT, false)} />
                </div>
            </div>
        );
    }

    /**
     * Render the scheduled activity table columns.
     *
     * Includes the actions column used to open scheduled org details.
     */
    function renderScheduledColumns() {
        return [
            <DataTableColumn key="createdBy" label={t('push_portal_scheduled_by')} property="createdBy" truncate>
                <DataTableCell>
                    {(item: any) => item.createdBy || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>,
            <DataTableColumn key="scheduledTimeFormatted" label={t('push_portal_scheduled_time')} property="scheduledTimeFormatted" primaryColumn />,
            <DataTableColumn key="upgradeLabel" label={t('push_portal_target_version')} property="upgradeLabel" truncate>
                <DataTableCell>
                    {(item: any) => item.upgradeLabel || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>,
            <DataTableColumn key="actions" label={t('push_portal_actions')} property="actions">
                <CustomActionsCell onView={setSelectedScheduledRequest} />
            </DataTableColumn>
        ];
    }

	const handleRowAction = (item: any, action: any) => {
		if (action.value === CQAppConstant.PUSH_PORTAL_UI.ROW_ACTIONS.ABORT) {
			setItemToAbort(item);
			setAbortConfirmOpen(true);
		}
	};
    /**
     * Render the shared activity table columns for paused, pending, and failed tabs.
     *
     * @param includeErrorColumn When true, renders the failed-tab error column
     * and failed-tab column ordering; otherwise renders the status column.
     * @param includePendingActions When true, includes row actions for pending tab.
     */
    function renderOrgActivityColumns(includeErrorColumn: boolean, includePendingActions?: boolean) {
        const columns = [
            <DataTableColumn key="orgId" label={t('push_portal_org_id')} property="orgId" truncate>
                <DataTableCell>
                    {(item: any) => item.orgId || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>,
            <DataTableColumn key="orgName" label={t('push_portal_org_name')} property="orgName" truncate >
                <DataTableCell>
                    {(item: any) => item.orgName || item.orgId || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>
        ];

        if (includeErrorColumn) {
            columns.push(
                <DataTableColumn key="errorMessage" label={t('push_portal_error_column')} property="errorMessage" truncate>
                    <DataTableCell>
                        {(item: any) => (
                            <span title={item.errorMessage || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}>
                                {item.errorMessage || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                            </span>
                        )}
                    </DataTableCell>
                </DataTableColumn>
            );
        } else {
            columns.push(<DataTableColumn key="submittedAtFormatted" label={t('push_portal_submitted_time')} property="submittedAtFormatted" />);
        }

        columns.push(
            <DataTableColumn key="stage" label={t('push_portal_stage')} property="stage" truncate>
                <DataTableCell>
                    {(item: any) => item.stage || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>
        );

        if (!includeErrorColumn) {
            columns.push(
                <DataTableColumn key="status" label={t('push_portal_status')} property="status" truncate>
                    <DataTableCell>
                        {(item: any) => item.status || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                    </DataTableCell>
                </DataTableColumn>
            );
        }

        if (includeErrorColumn) {
            columns.push(<DataTableColumn key="submittedAtFormatted" label={t('push_portal_submitted_time')} property="submittedAtFormatted" />);
        }

        columns.push(
            <DataTableColumn key="updatedAtFormatted" label={t('push_portal_updated_time')} property="updatedAtFormatted" />,
            <DataTableColumn key="upgradeLabel" label={t('push_portal_upgrade_path')} property="upgradeLabel" truncate>
                <DataTableCell>
                    {(item: any) => item.upgradeLabel || CQAppConstant.PUSH_PORTAL_UI.PLACEHOLDER_TEXT}
                </DataTableCell>
            </DataTableColumn>
        );

        if (includePendingActions) {
            columns.push(
                <DataTableRowActions
                    key={CQAppConstant.PUSH_PORTAL_UI.COLUMN_KEYS.ROW_ACTIONS}
                    options={[
                        {
                            label: t('push_portal_abort'),
                            value: CQAppConstant.PUSH_PORTAL_UI.ROW_ACTIONS.ABORT,
                          
                        },
                      
                    ]}
                    dropdown={<Dropdown length={CQAppConstant.PUSH_PORTAL_UI.DROPDOWN_LENGTH}/>}
                    onAction={handleRowAction as any}
                />
            );
        }

        return columns;
    }

	/**
	 * Execute the abort action after user confirmation.
	 * Calls the backend to abort the selected activity, then:
	 * - Closes the confirmation modal
	 * - Shows success/error toast message
	 * - Refreshes the activity list
	 */
	const handleAbortConfirm = async () => {
		if (!itemToAbort) return;
		setAbortLoading(true);
		try {
			await PushPortalServiceProvider.abortActivity(itemToAbort.orgId);
			setAbortConfirmOpen(false);
			setItemToAbort(null);
			setToastMessage(t('push_portal_abort_success', {
				orgName: itemToAbort.orgName || CQAppConstant.UNKNOWN_ORG,
				orgId: itemToAbort.orgId
			}));
			setToastVariant(CQAppConstant.ALERT_VARIANT.SUCCESS);
			setToastOpen(true);
			handleRefreshActivity();
		} catch (err: any) {
			setAbortConfirmOpen(false);
			setToastMessage(err?.response?.data?.error || t('push_portal_abort_failed', {
				orgName: itemToAbort.orgName || CQAppConstant.UNKNOWN_ORG,
				orgId: itemToAbort.orgId
			}));
			setToastVariant(CQAppConstant.ALERT_VARIANT.ERROR);
			setToastOpen(true);
			setItemToAbort(null);
		} finally {
			setAbortLoading(false);
		}
	};

    /**
     * Cancels the currently selected scheduled push request, either fully or for
     * specific orgs.
     *
     * @param orgIds Org IDs to cancel; when omitted, cancels every org on the request.
     */
    async function handleCancelScheduledRequest(orgIds?: string[]) {
        if (!selectedScheduledRequest) return;

        setCancelLoading(true);
        try {
            const updatedReq = await PushPortalServiceProvider.cancelPushRequest(selectedScheduledRequest.requestId, orgIds, currentUsername);
            setCancelConfirmOpen(false);
            setOrgIdsToCancel([]);
            setIsCancelAll(false);

            const cancelledSet = new Set(orgIds && orgIds.length > 0 ? orgIds : selectedScheduledRequest.orgIds);
            const remainingOrgs = (selectedScheduledRequest.orgs || []).filter((o) => !cancelledSet.has(o.orgId));

            if (!updatedReq || updatedReq.status === CQAppConstant.PUSH_PORTAL_UI.PUSH_REQUEST_STATUSES.CANCELLED || remainingOrgs.length === 0) {
                setSelectedScheduledRequest(null);
                setIsEditingScheduledTime(false);
                setEditScheduledError('');
            } else {
                setSelectedScheduledRequest(prev => prev ? {
                    ...prev,
                    ...updatedReq,
                    orgs: remainingOrgs
                } : null);
            }

            showToast(t('push_portal_cancel_success'), CQAppConstant.ALERT_VARIANT.SUCCESS);
            loadOrgs(orgPage);
            handleRefreshActivity();
        } catch (err: any) {
            setCancelConfirmOpen(false);
            setOrgIdsToCancel([]);
            setIsCancelAll(false);
            console.error('[PushPortal] Cancel scheduled request failed', err?.message || err);
            showToast(err?.response?.data?.error || err?.message || t('push_portal_cancel_failed'), CQAppConstant.ALERT_VARIANT.ERROR);
        } finally {
            setCancelLoading(false);
        }
    }

    /**
     * Handle editing and saving scheduled time.
     * When called without confirmation (isConfirmed = false), validates future time in UI and opens confirmation modal.
     * When called with confirmation (isConfirmed = true), executes backend patch, updates state in-place,
     * shows success/error toast, and reloads activity in background.
     */
    async function handleUpdateScheduledTime(isConfirmed?: boolean) {
        // Authoritative re-check against the current cutoff, regardless of what the
        // live re-check effect already did — protects against the popup having been
        // left open across the daily 03:30 UTC boundary.
        const isoTime = buildScheduledTimeIso(editScheduledDateTime);
        if (Number.isNaN(new Date(isoTime).getTime()) || new Date(isoTime).getTime() <= Date.now()) {
            setEditScheduledError(t('push_portal_schedule_time_future'));
            setEditScheduledDateTime(getMinSelectableUtcDate());
            if (isConfirmed) setEditScheduledConfirmOpen(false);
            return;
        }
        setEditScheduledError('');

        if (!isConfirmed) {
            setEditScheduledConfirmOpen(true);
            return;
        }

        if (!selectedScheduledRequest) return;
        setEditScheduledLoading(true);
        try {
            const updatedReq = await PushPortalServiceProvider.updatePushRequestScheduledTime(selectedScheduledRequest.requestId, isoTime);
            setEditScheduledConfirmOpen(false);
            setIsEditingScheduledTime(false);

            // Update state in-place so modal remains open with updated data
            setSelectedScheduledRequest(prev => prev ? {
                ...prev,
                ...updatedReq,
                scheduledTime: isoTime
            } : null);

            showToast(t('push_portal_schedule_time_update_success'), CQAppConstant.ALERT_VARIANT.SUCCESS);
            handleRefreshActivity();
        } catch (err: any) {
            setEditScheduledConfirmOpen(false);
            const errMsg = err?.response?.data?.error || t('push_portal_schedule_time_update_failed');
            setEditScheduledError(errMsg);
            showToast(errMsg, CQAppConstant.ALERT_VARIANT.ERROR);

            // The request may have moved out of SCHEDULED (e.g. claimed by the
            // scheduler for execution) between opening this modal and confirming.
            // Refresh the underlying record so the modal reflects the current
            // status and hides now-invalid edit/cancel controls, instead of
            // leaving the user to retry indefinitely against stale data.
            try {
                const latest = await PushPortalServiceProvider.getPushRequest(selectedScheduledRequest.requestId);
                setSelectedScheduledRequest(prev => prev ? { ...prev, ...latest } : latest);
                if (latest?.status !== CQAppConstant.PUSH_PORTAL_UI.PUSH_REQUEST_STATUSES.SCHEDULED) {
                    setIsEditingScheduledTime(false);
                }
            } catch {
                // Best-effort refresh; ignore failures here, the error toast above already fired.
            }
        } finally {
            setEditScheduledLoading(false);
        }
    }

    /**
     * Opens the cancel confirmation modal for a single org, or for all orgs on the
     * currently selected scheduled push request when `orgId` is omitted.
     *
     * @param orgId Org ID to cancel; when omitted, targets all orgs (cancel-all).
     */
    function handleCancelClick(orgId?: string) {
        if (!selectedScheduledRequest) return;
        
        if (orgId) {
            setOrgIdsToCancel([orgId]);
            setIsCancelAll(false);
        } else {
            const orgs = selectedScheduledRequest.orgs;
            setOrgIdsToCancel(orgs?.map(o => o.orgId) || []);
            setIsCancelAll(true);
        }
        setCancelConfirmOpen(true);
    }

    /**
     * Render an activity tab using shared state/config helpers.
     *
     * Handles the empty state, table shell, and pager for the given tab.
     *
     * @param tab Activity tab identifier.
     */
    function renderActivityTab(tab: ActivityTab) {
        const state = getActivityState(tab);
        const config = getActivityConfig(tab);

        if (state.items.length === 0) {
            return (
                <div className="slds-text-body_regular slds-text-color_weak slds-p-around_medium slds-text-align_center">
                    {config.emptyMessage}
                </div>
            );
        }

        return (
            <div>
                <div className="slds-scrollable_x">
                    <IconSettings iconPath="/assets/icons">
                        <DataTable
                            items={state.items}
                            id={config.tableId}
                            fixedLayout
                            columnBordered
                            noRowHover
                            resizable
                            {...(config.primaryColumn ? { primaryColumn: config.primaryColumn } : {})}
                        >
                            {config.renderColumns()}
                        </DataTable>
                    </IconSettings>
                </div>
                {renderPager(tab)}
            </div>
        );
    }

    // Render scheduled activity tab content.
    function renderScheduledTab() {
        return renderActivityTab(CQAppConstant.ACTIVITY_TAB.SCHEDULED);
    }

    // Render paused activity tab content.
    function renderPausedTab() {
        return renderActivityTab(CQAppConstant.ACTIVITY_TAB.PAUSED);
    }

    // Render pending activity tab content.
    function renderPendingTab() {
        return renderActivityTab(CQAppConstant.ACTIVITY_TAB.PENDING);
    }

    // Render failed activity tab content.
    function renderFailedTab() {
        return renderActivityTab(CQAppConstant.ACTIVITY_TAB.FAILED);
    }

    /** Load version filter metadata for update/upgrade scheduling. */
    async function loadVersionFilters() {
        try {
            const filters = await PushPortalServiceProvider.getVersionFilters();
            const safeFilters = {
                upgradeVersions: filters?.upgradeVersions ?? [],
                updateVersions: filters?.updateVersions ?? [],
                updateModulesByVersion: filters?.updateModulesByVersion ?? {},
                upgradeModulesByVersionName: filters?.upgradeModulesByVersionName ?? {},
            };
            setVersionFilters(safeFilters);
            if ((safeFilters.updateVersions?.length ?? 0) > 0) {
                setFilterMode(CQAppConstant.FILTER_MODE.UPDATE);
                setFilterVersion('');
            } else if ((safeFilters.upgradeVersions?.length ?? 0) > 0) {
                setFilterMode(CQAppConstant.FILTER_MODE.UPGRADE);
                setFilterVersion('');
            } 
        } catch (err) {
            console.error('Failed to load version filters', err);
        }
    }

    // Update selection map for org scheduling.
    function toggleOrgSelection(orgId: string, checked: boolean) {
        setSelectedOrgs(prev => {
            const next = new Map(prev);
            if (checked) {
                const {orgName, orgType} = resolveOrgNameAndType(orgId);
                if (orgName) next.set(orgId, {orgName: orgName , orgType: orgType });
            } else {
                next.delete(orgId);
            }
            return next;
        });
    }

    // Remove orgs from selection state after deletion.
    function handleRemoveFromSelection(orgIdsToRemove: string[]) {
        setSelectedOrgs(prev => {
            const next = new Map(prev);
            orgIdsToRemove.forEach(orgId => {
                next.delete(orgId);
            });
            return next;
        });
    }

    /**
     * Select all orgs listed on the current page when filters are active.
     */
    function handleSelectAllFilteredOrgs() {
        const effectiveSearch = orgSearch.trim();
        const isFilterActive = !!effectiveSearch || orgTypeFilter !== CQAppConstant.FILTER_MODE.ALL || !!filterVersion;
        if (!isFilterActive || !canSchedule || !filterVersion) return;

        setSelectAllLoading(true);
        const nextSelected = new Map<string, { orgName: string; orgType: string }>();
        const nextOrgNames: Record<string, string> = {};
        orgEntries.forEach(entry => {
            if (entry.org?.orgId) {
                nextSelected.set(entry.org.orgId, { orgName: entry.org.orgName || entry.org.orgId, orgType: entry.org.orgType});
                nextOrgNames[entry.org.orgId] = entry.org.orgName;
            }
            if (orgTypeFilter !== CQAppConstant.ORG_TYPE.PRODUCTION) {
                (entry.childSandboxes ?? []).forEach(sandbox => {
                    if (sandbox.orgId) {
                        nextSelected.set(sandbox.orgId, {orgName: sandbox.orgName || sandbox.orgId, orgType: sandbox.orgType});
                        nextOrgNames[sandbox.orgId] = sandbox.orgName;
                    }
                });
            }
        });
        setSelectedOrgs(nextSelected);
        setOrgNamesById(prev => ({ ...prev, ...nextOrgNames }));
        setSelectAllLoading(false);
    }

    // Reset filters and selections when mode changes.
    function handleFilterModeChange(mode: typeof CQAppConstant.FILTER_MODE.UPDATE | typeof CQAppConstant.FILTER_MODE.UPGRADE) {
        setFilterMode(mode);
        setFilterVersion('');
        setSelectedOrgs(new Map());
        loadOrgs(1, undefined, mode, '');
    }

    /**
     * Show a toast notification with the supplied message + variant.
     */
    function notify(message: string, variant: typeof CQAppConstant.ALERT_VARIANT.SUCCESS | typeof CQAppConstant.ALERT_VARIANT.ERROR) {
        setToastMessage(message);
        setToastVariant(variant);
        setToastOpen(true);
    }

    // Note: All filtering (orgType, version) is now handled server-side in loadOrgs()

    /** Resolve a user-friendly org name and org type for an orgId (searches parent + child sandboxes on current page). */
    function resolveOrgNameAndType(orgId: string): { orgName: string; orgType: string } {
        for (const entry of orgEntries) {
            if (entry.org?.orgId === orgId) {
                return {
                    orgName: entry.org.orgName,
                    orgType: entry.org.orgType || CQAppConstant.UNKNOWN_ORG
                };
            }
            const sandbox = entry.childSandboxes?.find(s => s.orgId === orgId);
            if (sandbox) {
                return {
                    orgName: sandbox.orgName,
                    orgType: sandbox.orgType || CQAppConstant.UNKNOWN_ORG
                };
            }
        }
        return { orgName: orgNamesById[orgId] || '', orgType: CQAppConstant.UNKNOWN_ORG };
    }

    /** Build a stable, sorted list of selected orgs for display in the schedule modal. */
    function getSelectedOrgsForDisplay(): { orgId: string; orgName: string; orgType: string }[] {
        const isProduction = (type: string) => type === CQAppConstant.ORG_TYPE.PRODUCTION;

        return Array.from(selectedOrgs.entries())
            .map(([orgId, data]) => ({ orgId, orgName: data.orgName, orgType: data.orgType }))
            .sort((a, b) => {
                // 1. Production orgs always first
                if (isProduction(a.orgType) && !isProduction(b.orgType)) return -1;
                if (!isProduction(a.orgType) && isProduction(b.orgType)) return 1;

                // 2. Within same tier, sort by name (with ID fallback)
                return (a.orgName || a.orgId).localeCompare(b.orgName || b.orgId);
            });
    }

    /** Resolve the target module payload for the selected version filter (update/upgrade). */
    function getTargetModule() {
        if (!filterVersion) return null;
        return filterMode === CQAppConstant.FILTER_MODE.UPDATE
            ? versionFilters?.updateModulesByVersion?.[filterVersion]
            : versionFilters?.upgradeModulesByVersionName?.[filterVersion];
    }

    /** Resolve a display label for the selected update/upgrade module. */
    function getTargetLabel(module: any): string {
        if (!module) return '';
        return module.name || module.versionName || module.moduleName || '';
    }

    /**
     * Show a transient toast notification in the Push Portal UI.
     */
    function showToast(message: string, variant: typeof CQAppConstant.ALERT_VARIANT.SUCCESS | typeof CQAppConstant.ALERT_VARIANT.ERROR | typeof CQAppConstant.ALERT_VARIANT.WARNING) {
        setToastMessage(message);
        setToastVariant(variant);
        setToastOpen(true);
    }

    /**
     * Create a push request for all currently selected orgIds.
     *
     * On success:
     * - clears selection
     * - reloads orgs
     */
    async function handleSchedule() {
        // Prevent re-entrancy from rapid double-clicks.
        if (scheduleLoading) return;

        if (!filterVersion) {
            setScheduleError(t('push_portal_select_version'));
            return;
        }
        if (selectedOrgs.size === 0) {
            setScheduleError(CQAppConstant.ERROR_MESSAGES.SELECT_AT_LEAST_ONE_ORG);
            return;
        }
        const module = filterMode === CQAppConstant.FILTER_MODE.UPDATE
            ? versionFilters?.updateModulesByVersion?.[filterVersion]
            : versionFilters?.upgradeModulesByVersionName?.[filterVersion];
        if (!module) {
            setScheduleError(t('push_portal_no_upgrade_module'));
            return;
        }

        // Authoritative re-check against the current cutoff, regardless of what the
        // live re-check effect already did — protects against the popup having been
        // left open across the daily 03:30 UTC boundary.
        const scheduledTimeIso = buildScheduledTimeIso(scheduledDateTime);
        if (Number.isNaN(new Date(scheduledTimeIso).getTime()) || new Date(scheduledTimeIso).getTime() <= Date.now()) {
            setScheduleError(t('push_portal_schedule_time_future'));
            setScheduledDateTime(getMinSelectableUtcDate());
            return;
        }

        // Set loading state before any awaited calls to disable buttons immediately.
        setScheduleLoading(true);
        setScheduleError('');
        setScheduleResults(null);

        // Check if session is still valid before proceeding
        const isValid = await SFConnection.isSessionValid();
        if (!isValid) {
            setScheduleLoading(false);
            window.dispatchEvent(new CustomEvent('session-expired'));
            return;
        }

        const orgIds = Array.from(selectedOrgs.keys());
        try {
            const response = await PushPortalServiceProvider.createPushRequest({
                orgIds,
                createdBy: currentUsername,
                createdByEmail: currentUserEmail,
                scheduledTime: scheduledTimeIso,
                module,
            });
            const results: ScheduleResult[] = response?.results?.length
                ? response.results
                : orgIds.map(orgId => ({ orgId, success: true }));
            const successCount = results.filter(result => result.success ?? !result.error).length;
            const failureCount = results.length - successCount;
            setScheduleResults({ results });
            if (failureCount > 0) {
                showToast(t('push_portal_schedule_partial_success', { successCount, failureCount }), CQAppConstant.ALERT_VARIANT.WARNING);
                loadOrgs();
                handleRefreshActivity();
                setScheduleLoading(false);
                return;
            }
            showToast(t('push_portal_schedule_success'), CQAppConstant.ALERT_VARIANT.SUCCESS);
            setScheduleLoading(false);
            setScheduleOpen(false);
            setScheduleResults(null);
            setSelectedOrgs(new Map());
            loadOrgs();
            handleRefreshActivity();
        } catch (err: any) {
            const data = err?.response?.data || {};
            if (data.synced === true) {
                loadOrgs(orgPage);
            }
            setScheduleResults({
                error: data.error || t('push_portal_schedule_failed'),
                results: data.results || data.failures
            });
            showToast(data.error || t('push_portal_schedule_failed'), CQAppConstant.ALERT_VARIANT.ERROR);
            setScheduleLoading(false);
        }
    }


    /**
     * Load the org list with server-side pagination.
     * Uses request gating so older responses cannot overwrite newer results.
     * 
     * @param page - Page number to load
     * @param orgType - Optional orgType to use instead of state (for handling async state updates)
     * @param versionMode - Optional filterMode to use instead of state
     * @param versionFilter - Optional filterVersion to use instead of state
     */
    async function loadOrgs(page: number = 1, orgType?: string, versionMode?: string, versionFilter?: string, search?: string) {
        setIsLoading(true);
        const requestId = ++loadOrgsRequestIdRef.current;
        try {
            const effectiveSearch = search !== undefined ? search : orgSearch.trim();
            const effectiveOrgType = orgType !== undefined ? orgType : orgTypeFilter;
            const effectiveFilterMode = versionMode !== undefined ? versionMode : filterMode;
            const effectiveFilterVersion = versionFilter !== undefined ? versionFilter : filterVersion;
            const isFiltered = !!effectiveSearch || effectiveOrgType !== CQAppConstant.FILTER_MODE.ALL || !!effectiveFilterVersion;
            
            // applicableUpgrades are now resolved server-side from filterVersion to avoid URL length limits
            const res = await PushPortalServiceProvider.listOrgs({
                page,
                pageSize: CQAppConstant.ORG_LIST_PAGE_SIZE,
                search: effectiveSearch || undefined,
                orgType: effectiveOrgType !== CQAppConstant.FILTER_MODE.ALL ? effectiveOrgType : undefined,
                filterMode: effectiveFilterVersion ? effectiveFilterMode : undefined,
                filterVersion: effectiveFilterVersion || undefined,
            });
            if (requestId !== loadOrgsRequestIdRef.current) return;
            const items = res?.items ?? [];
            setOrgEntries(items);
            setOrgNamesById(prev => {
                const next = { ...prev };
                items.forEach(entry => {
                    if (entry.org?.orgId) next[entry.org.orgId] = entry.org.orgName;
                    (entry.childSandboxes ?? []).forEach(sandbox => {
                        if (sandbox.orgId) next[sandbox.orgId] = sandbox.orgName;
                    });
                });
                return next;
            });
            setOrgPage(res?.page ?? page);
            setOrgTotalPages(res?.totalPages ?? 1);
            setOrgTotal(res?.total ?? 0);
            setOrgHasPrev(res?.hasPrev ?? false);
            setOrgHasNext(res?.hasNext ?? false);
            if (!isFiltered) {
                setHasSearched(false);
            } else {
                setHasSearched(true);
            }
        } catch (err) {
            if (requestId !== loadOrgsRequestIdRef.current) return;
            console.error('Failed to load orgs:', err);
        } finally {
            if (requestId !== loadOrgsRequestIdRef.current) return;
            setIsLoading(false);
        }
    }

    /**
     * Reset all org list filters and reload the first page.
     *
     * Clears search text, org type, and version filter, then
     * triggers a fresh org load so the list reflects the reset state.
     */
    function handleClearFilters() {
        setOrgSearch('');
        setOrgTypeFilter(CQAppConstant.FILTER_MODE.ALL);
        setFilterVersion('');
        setHasSearched(false);
        loadOrgs(1, CQAppConstant.FILTER_MODE.ALL, filterMode, '', '');
    }

    return (
        <IconSettings iconPath="/assets/icons">
            <div className="slds-p-top_medium">

                <Card
                    className="slds-m-bottom_large"
                    heading={t('push_portal_upgrade_activity')}
                    headerActions={
                        <div style={{ width: '300px' }}>
                            <div className="slds-form-element">
                                <div className="slds-form-element__control">
                                    <div className="slds-input-has-icon slds-input-has-icon_right">
                                        <input
                                            type="text"
                                            className="slds-input"
                                            placeholder={t('push_portal_activity_search_placeholder')}
                                            value={activitySearch}
                                            onChange={(event: any) => setActivitySearch(event.target.value)}
                                        />
                                        {activitySearch && (
                                            <Button
                                                assistiveText={{ icon: 'Clear' }}
                                                iconCategory="utility"
                                                iconName="clear"
                                                iconSize="x-small"
                                                iconVariant="bare"
                                                variant="icon"
                                                className="slds-input__icon slds-input__icon_right"
                                                title={t('push_portal_clear')}
                                                onClick={() => setActivitySearch('')}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                >
                    <div className="slds-p-around_small">
                        <Tabs
                            id="upgrade-activity-tabs"
                            onSelect={(index: number) => {
                                const tabs = [CQAppConstant.ACTIVITY_TAB.PENDING, CQAppConstant.ACTIVITY_TAB.FAILED, CQAppConstant.ACTIVITY_TAB.PAUSED, CQAppConstant.ACTIVITY_TAB.SCHEDULED] as const;
                                const selectedTab = tabs[index];
                                setSummaryTab(selectedTab);
                            }}
                            selectedIndex={['pending', 'failed', 'paused', 'scheduled'].indexOf(summaryTab)}
                        >
                        <TabsPanel label={CQAppConstant.TAB_LABELS.PENDING}>
                            {upgradeSummaryLoading ? (
                                <div className="slds-text-align_center slds-p-around_medium">
                                    <CQSpinner />
                                </div>
                            ) : (
                                renderPendingTab()
                            )}
                        </TabsPanel>
                        <TabsPanel label={CQAppConstant.TAB_LABELS.FAILED}>
                            {upgradeSummaryLoading ? (
                                <div className="slds-text-align_center slds-p-around_medium">
                                    <CQSpinner />
                                </div>
                            ) : (
                                renderFailedTab()
                            )}
                        </TabsPanel>
                        <TabsPanel label={CQAppConstant.TAB_LABELS.PAUSED}>
                            {upgradeSummaryLoading ? (
                                <div className="slds-text-align_center slds-p-around_medium">
                                    <CQSpinner />
                                </div>
                            ) : (
                                renderPausedTab()
                            )}
                        </TabsPanel>
                        <TabsPanel label={CQAppConstant.TAB_LABELS.SCHEDULED}>
                            {upgradeSummaryLoading ? (
                                <div className="slds-text-align_center slds-p-around_medium">
                                    <CQSpinner />
                                </div>
                            ) : (
                                renderScheduledTab()
                            )}
                        </TabsPanel>
                    </Tabs>
                    </div>
                </Card>

                {canSchedule && (
                    <div className="slds-box slds-box_small slds-theme_default slds-m-bottom_medium">
                        <div className="slds-grid slds-wrap slds-gutters_small slds-grid_vertical-align-end">
                            <div className="slds-col slds-size_1-of-1 slds-medium-size_2-of-12">
                                <fieldset className="slds-form-element">
                                    <legend className="slds-form-element__legend slds-form-element__label">{t('push_portal_filter_mode')}</legend>
                                    <div className="slds-form-element__control">
                                        <div className="slds-radio_button-group">
                                            <span className="slds-button slds-radio_button">
                                                <input
                                                    type="radio"
                                                    id={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_MODE_UPDATE}
                                                    name="filter-mode"
                                                    value={CQAppConstant.FILTER_MODE.UPDATE}
                                                    checked={filterMode === CQAppConstant.FILTER_MODE.UPDATE }
                                                    onChange={e => handleFilterModeChange(e.target.value as 'update' | 'upgrade')}
                                                />
                                                <label className="slds-radio_button__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_MODE_UPDATE}>
                                                    <span className="slds-radio_faux">{t('push_portal_update')}</span>
                                                </label>
                                            </span>
                                            <span className="slds-button slds-radio_button">
                                                <input
                                                    type="radio"
                                                    id={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_MODE_UPGRADE}
                                                    name="filter-mode"
                                                    value={CQAppConstant.FILTER_MODE.UPGRADE}
                                                    checked={filterMode === CQAppConstant.FILTER_MODE.UPGRADE}
                                                    onChange={e => handleFilterModeChange(e.target.value as 'update' | 'upgrade')}
                                                />
                                                <label className="slds-radio_button__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_MODE_UPGRADE}>
                                                    <span className="slds-radio_faux">{t('push_portal_upgrade')}</span>
                                                </label>
                                            </span>
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                            <div className="slds-col slds-size_1-of-1 slds-medium-size_3-of-12">
                                <div className="slds-form-element">
                                    <label className="slds-form-element__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_VERSION}>
                                        {filterMode === CQAppConstant.FILTER_MODE.UPDATE ? t('push_portal_update_version') : t('push_portal_upgrade_version')}
                                    </label>
                                    <div className="slds-form-element__control">
                                        <select
                                            id={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_VERSION}
                                            className="slds-select"
                                            value={filterVersion}
                                            onChange={e => { 
                                                const newVersion = e.target.value;
                                                setFilterVersion(newVersion); 
                                                setSelectedOrgs(new Map());
                                                loadOrgs(1, undefined, filterMode, newVersion);
                                            }}
                                        >
                                            <option value="">{t('push_portal_all_versions')}</option>
                                            {versionFilters && (filterMode === CQAppConstant.FILTER_MODE.UPDATE ? versionFilters.updateVersions : versionFilters.upgradeVersions).map(v => (
                                                <option key={v} value={v}>{v}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="slds-col slds-size_1-of-1 slds-medium-size_2-of-12">
                                <div className="slds-form-element">
                                    <label className="slds-form-element__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_ORG_TYPE}>{t('push_portal_org_type')}</label>
                                    <div className="slds-form-element__control">
                                        <select
                                            id={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_ORG_TYPE}
                                            className="slds-select"
                                            value={orgTypeFilter}
                                            onChange={e => { 
                                                const newOrgType = e.target.value as OrgTypeFilter;
                                                setOrgTypeFilter(newOrgType); 
                                                setSelectedOrgs(new Map());
                                                loadOrgs(1, newOrgType);
                                            }}
                                        >
                                            <option value={CQAppConstant.FILTER_MODE.ALL}>{t('push_portal_all')}</option>
                                            <option value={CQAppConstant.ORG_TYPE.PRODUCTION}>{t('push_portal_production')}</option>
                                            <option value={CQAppConstant.ORG_TYPE.SANDBOX}>{t('push_portal_sandbox')}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="slds-col slds-size_1-of-1 slds-medium-size_3-of-12">
                                <div className="slds-form-element">
                                    <label className="slds-form-element__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_ORG_SEARCH}>{t('push_portal_search')}</label>
                                    <div className="slds-form-element__control slds-input-has-icon slds-input-has-icon_right">
                                        {orgSearch ? (
                                            <button
                                                className="slds-button slds-button_icon slds-input__icon slds-input__icon_right"
                                                title={t('push_portal_clear')}
                                                onClick={() => setOrgSearch('')}
                                            >
                                                <Icon
                                                    category="utility"
                                                    name="clear"
                                                    size="x-small"
                                                />
                                                <span className="slds-assistive-text">{t('push_portal_clear')}</span>
                                            </button>
                                        ) : (
                                            <Icon
                                                assistiveText={{ label: t('push_portal_search') }}
                                                category="utility"
                                                name="search"
                                                size="x-small"
                                                className="slds-icon slds-input__icon slds-input__icon_right slds-icon-text-default"
                                            />
                                        )}
                                        <input
                                            id={CQAppConstant.PUSH_PORTAL_UI.IDS.FILTER_ORG_SEARCH}
                                            className="slds-input"
                                            type="text"
                                            value={orgSearch}
                                            placeholder={t('push_portal_search_placeholder')}
                                            onChange={e => setOrgSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="slds-col slds-size_1-of-1 slds-medium-size_2-of-12 slds-grid slds-grid_align-end">
                                <Button
                                    label={`Schedule (${selectedOrgs.size})`}
                                    variant="brand"
                                    disabled={selectedOrgs.size === 0 || !filterVersion}
                                    onClick={() => {
                                        setScheduleError('');
                                        setScheduledDateTime(getMinSelectableUtcDate());
                                        setScheduleOpen(true);
                                    }}
                                    className="slds-m-top_medium"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="slds-box slds-box_small slds-theme_default">
                    <h3 className="slds-text-heading_small slds-text-title_bold slds-m-bottom_medium">{t('push_portal_registered_orgs')}</h3>
                    {isLoading ? (
                        <div className="slds-text-align_center slds-p-around_large">
                            <CQSpinner />
                        </div>
                    ) : orgTotal === 0 ? (
                    !hasSearched?
                    <div className="slds-illustration slds-illustration_small slds-p-around_large slds-text-align_center">
                        <div className="slds-m-vertical_medium">
                            <svg className="slds-icon slds-icon_large slds-icon-text-default" aria-hidden="true">
                                <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#connected_apps" />
                            </svg>
                        </div>
                        <h3 className="slds-text-heading_medium">{t('push_portal_no_orgs_registered')}</h3>
                        <p className="slds-text-body_regular slds-text-color_weak">
                            {t('push_portal_no_orgs_registered_description')}
                        </p>
                    </div>:
                    <div className="slds-illustration slds-illustration_small slds-p-around_large slds-text-align_center">
                        <div className="slds-m-vertical_medium">
                            <div className="slds-is-relative slds-inline-block">
                                <svg className="slds-icon slds-icon_large slds-icon-text-default" aria-hidden="true">
                                    <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#search" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="slds-text-heading_medium">{t('no_matching_records')}</h3>
                        <p className="slds-text-body_regular slds-text-color_weak">
                            {t('no_matching_records_description')}
                        </p>
                        <div className="slds-m-top_medium">
                            <Button
                                label={t('reset_filters')}
                                variant="neutral"
                                onClick={() => handleClearFilters()}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-bottom_small">
                            <div className="slds-text-body_small slds-text-color_weak">
                                {t('push_portal_page_of', { page: orgPage, total: orgTotalPages })}
                            </div>
                            <div>
                                {(!!orgSearch.trim() || orgTypeFilter !== CQAppConstant.FILTER_MODE.ALL || !!filterVersion) && canSchedule && filterVersion && (
                                    <Button
                                        label={selectAllLoading ? t('push_portal_selecting') : t('push_portal_select_all_listed')}
                                        variant="neutral"
                                        disabled={selectAllLoading || isLoading}
                                        onClick={handleSelectAllFilteredOrgs}
                                        className="slds-m-right_x-small"
                                    />
                                )}
                                <Button
                                    label={t('push_portal_prev')}
                                    variant="neutral"
                                    disabled={!orgHasPrev || isLoading}
                                    onClick={() => {
                                        const newPage = orgPage - 1;
                                        setOrgPage(newPage);
                                        loadOrgs(newPage);
                                    }}
                                />
                                <Button
                                    label={t('push_portal_next')}
                                    variant="neutral"
                                    disabled={!orgHasNext || isLoading}
                                    onClick={() => {
                                        const newPage = orgPage + 1;
                                        setOrgPage(newPage);
                                        loadOrgs(newPage);
                                    }}
                                    className="slds-m-left_x-small"
                                />
                            </div>
                        </div>
                        {orgEntries.map((entry,index) => (
                                <OrgRow
                                    index={index}
                                    pageNo={orgPage}
                                    key={entry.org.orgId}
                                    entry={entry}
                                    currentUsername={currentUsername}
                                    onRefresh={() => loadOrgs(orgPage)}
                                    onNotify={notify}
                                    onRemoveFromSelection={handleRemoveFromSelection}
                                    canRegister={canRegister}
                                    selectable={canSchedule && filterVersion !== ''}
                                    selectedOrgIds={selectedOrgs}
                                    onSelect={toggleOrgSelection}
                                    orgTypeFilter={orgTypeFilter}

                                />
                        ))}
                        <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-bottom_small">
                            <div className="slds-text-body_small slds-text-color_weak">
                                {t('push_portal_page_of', { page: orgPage, total: orgTotalPages })}
                            </div>
                            <div>
                                <Button
                                    label={t('push_portal_prev')}
                                    variant="neutral"
                                    disabled={!orgHasPrev || isLoading}
                                    onClick={() => {
                                        const newPage = orgPage - 1;
                                        setOrgPage(newPage);
                                        loadOrgs(newPage);
                                    }}
                                />
                                <Button
                                    label={t('push_portal_next')}
                                    variant="neutral"
                                    disabled={!orgHasNext || isLoading}
                                    onClick={() => {
                                        const newPage = orgPage + 1;
                                        setOrgPage(newPage);
                                        loadOrgs(newPage);
                                    }}
                                    className="slds-m-left_x-small"
                                />
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>
            {(isRegisterOpen || showPackageInterstitial) && (
                <div className="slds-modal slds-fade-in-open" role="dialog">
                    <div className="slds-modal__container">
                        {showPackageInterstitial ? (
                            <CQManagedPackageInterstitial
                                onInstallNow={handlePackageInstallNow}
                                onContinue={handlePackageContinue}
                                onBack={handlePackageBack}
                                isVisible={true}
                            />
                        ) : (
                            <>
                                <header className="slds-modal__header">
                                    <h2 className="slds-text-heading_medium">{t('connect_sf_org_title')}</h2>
                                </header>
                                <div className="slds-modal__content slds-p-around_medium">
                                    <p className="slds-text-body_small slds-text-color_weak slds-m-bottom_medium">
                                        {t('connect_sf_org_note')}
                                    </p>
                                    {regError && (
                                        <Alert
                                            labels={{ heading: `${t('push_portal_error')}: ${regError}` }}
                                            variant="error"
                                            dismissible={false}
                                            className="slds-m-bottom_small"
                                        />
                                    )}
                                    <div className="slds-form-element slds-size_1-of-3">
                                        <label className="slds-form-element__label" htmlFor={CQAppConstant.PUSH_PORTAL_UI.IDS.OAUTH_LOGINURL}>{t('connect_sf_org_type_label')}</label>
                                        <div className="slds-form-element__control">
                                            <select
                                                id={CQAppConstant.PUSH_PORTAL_UI.IDS.OAUTH_LOGINURL}
                                                className="slds-select"
                                                value={oauthLoginUrl}
                                                onChange={e => setOauthLoginUrl(e.target.value)}
                                            >
                                                <option value="https://login.salesforce.com">{t('connect_sf_org_production')}</option>
                                                <option value="https://test.salesforce.com">{t('connect_sf_org_sandbox')}</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="slds-form-element slds-m-top_medium">
                                        <label className="slds-form-element__label" htmlFor="push-portal-tag-names">
                                            {t('push_portal_tags_comma_separated')}
                                        </label>
                                        <div className="slds-form-element__control">
                                            <Input
                                                id="push-portal-tag-names"
                                                placeholder="QA, Finance, Pilot"
                                                value={registerTagNames}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRegisterTagNames(event.target.value)}
                                                onBlur={(event: React.ChangeEvent<HTMLInputElement>) => setRegisterTagNames(normalizeTagNames(event.target.value))}
                                            />
                                        </div>
                                        {registerTagError && (
                                            <div className="slds-text-color_error slds-text-body_small slds-m-top_x-small">
                                                {registerTagError}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <footer className="slds-modal__footer">
                                    <Button
                                        label={CQAppConstant.BUTTON_LABELS.CANCEL}
                                        variant="neutral"
                                        onClick={() => setIsRegisterOpen(false)}
                                    />
                                    <Button
                                        label={regLoading ? CQAppConstant.BUTTON_LABELS.CONNECTING : CQAppConstant.BUTTON_LABELS.CONNECT}
                                        variant="brand"
                                        iconCategory="utility"
                                        iconName="salesforce1"
                                        iconPosition="left"
                                        disabled={regLoading}
                                        onClick={handleConnectOrg}
                                        className="slds-m-left_x-small"
                                    />
                                </footer>
                            </>
                        )}
                    </div>
                </div>
            )}
            {(isRegisterOpen || showPackageInterstitial) && <div className="slds-backdrop slds-backdrop_open"></div>}
            <CQConfirmationModal
                isOpen={scheduleOpen}
                heading={t('push_portal_schedule_upgrade')}
                message={
                    <div>
                        {scheduleError && (
                            <Alert
                                labels={{ heading: `${t('push_portal_error')}: ${scheduleError}` }}
                                variant="error"
                                dismissible={false}
                                className="slds-m-bottom_small"
                            />
                        )}
                        <div className="slds-box slds-box_x-small slds-theme_shade slds-media slds-media_center slds-m-bottom_medium">
                            <div className="slds-media__figure">
                                <Icon category="utility" name="clock" size="x-small" className="slds-icon-text-warning" />
                            </div>
                            <div className="slds-media__body">
                                <p className="slds-text-body_small">
                                    {t('push_portal_scheduler_daily_note', { time: CQAppConstant.PUSH_PORTAL_UI.SCHEDULER_RUN_UTC.DISPLAY })}
                                </p>
                            </div>
                        </div>
                        {(() => {
                            const module = getTargetModule();
                            const label = getTargetLabel(module);
                            return (
                                <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-start slds-wrap">
                                    <div>
                                        <div className="slds-text-title_caps slds-m-bottom_xx-small">{t('push_portal_target')}</div>
                                        <span className="slds-box slds-box_xx-small slds-theme_shade slds-text-body_small">
                                            {`${filterMode === CQAppConstant.FILTER_MODE.UPDATE ? t('push_portal_update') : t('push_portal_upgrade')}${filterVersion ? ` — ${filterVersion}` : ''}${label ? ` — ${label}` : ''}`}
                                        </span>
                                    </div>
                                    <div>
                                        <div className="slds-text-title_caps slds-m-bottom_xx-small" id="scheduled-time-label">{t('push_portal_scheduled_time')}</div>
                                        <input
                                            id="scheduled-time-input"
                                            aria-labelledby="scheduled-time-label"
                                            type="date"
                                            className="slds-input"
                                            min={getMinSelectableUtcDate()}
                                            value={scheduledDateTime}
                                            onChange={(e) => setScheduledDateTime(e.target.value)}
                                            disabled={scheduleLoading}
                                            required
                                        />
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                }
                confirmLabel={t('push_portal_schedule')}
                cancelLabel={t('push_portal_close')}
                confirmActionStyle="brand"
                isActionInProgress={scheduleLoading}
                disableConfirmButton={!!scheduleResults}
                progressLabel={t('push_portal_scheduling')}
                supportingDetails={
                    <div>
                        <div className="slds-m-bottom_small">
                            <div className="slds-text-title_caps slds-m-bottom_x-small">{t('push_portal_selected_orgs')}</div>
                            <div className="slds-scrollable_x">
                                <DataTable
                                    items={getSelectedOrgsForDisplay().map((o, index) => ({ ...o, sn: index + 1 }))}
                                    id="selected-orgs-table"
                                    columnBordered
                                >
                                    <DataTableColumn key="sn" label={t('sn')} property="sn" />
                                    <DataTableColumn key="orgName" label={t('push_portal_org_name')} property="orgName" primaryColumn truncate />
                                    <DataTableColumn key="orgType" label={t('push_portal_org_type')} property="orgType" truncate />
                                    <DataTableColumn key="orgId" label={t('push_portal_org_id')} property="orgId" truncate />
                                </DataTable>
                            </div>
                        </div>
                        {(scheduleResults?.results && scheduleResults.results.length > 0) ? (
                            <div className="slds-m-top_small">
                                {scheduleResults.results.map(r => {
                                    const isSuccess = r.success ?? !r.error;
                                    return (
                                        <div key={r.orgId} className={`slds-text-body_small ${isSuccess ? 'slds-text-color_success' : 'slds-text-color_error'}`}>
                                            {isSuccess ? '✓' : '✗'} {r.orgId}{r.error ? ` — ${r.error}` : ''}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : scheduleResults &&
                        (
                            <div className="slds-m-top_small slds-text-color_error">
                                ✗{scheduleResults?.error}
                            </div>
                        )
                        }
                    </div>
                }
                onConfirm={handleSchedule}
                onCancel={() => { setScheduleOpen(false); setScheduleResults(null); scheduleResults && setSelectedOrgs(new Map()); }}
            />
            <CQConfirmationModal
                isOpen={!!selectedScheduledRequest}
                heading={t('push_portal_scheduled_orgs')}
                message={
                    selectedScheduledRequest && (
                        <div>
                            <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-start slds-wrap slds-m-bottom_medium">
                                <div>
                                    <div className="slds-text-title_caps slds-m-bottom_xx-small">{t('push_portal_scheduled_by')}</div>
                                    <div className="slds-text-body_regular">{selectedScheduledRequest.createdBy || '—'}</div>
                                </div>
                                <div className="slds-text-align_right">
                                    <div className="slds-text-title_caps slds-m-bottom_xx-small">{t('push_portal_target_version')}</div>
                                    <span className="slds-box slds-box_xx-small slds-theme_shade slds-text-body_small">
                                        {(selectedScheduledRequest as any).upgradeLabel || '—'}
                                    </span>
                                </div>
                            </div>
                            <div className="slds-text-title_caps slds-m-bottom_xx-small" id="view-scheduled-time-label">{t('push_portal_scheduled_time')}</div>
                            {!isEditingScheduledTime ? (
                                <div className="slds-grid slds-grid_vertical-align-center">
                                    <span>
                                        {selectedScheduledRequest.scheduledTime
                                            ? formatScheduledDateDisplay(selectedScheduledRequest.scheduledTime)
                                            : formatScheduledDateDisplay(selectedScheduledRequest.createdAt)}
                                    </span>
                                    {(selectedScheduledRequest?.status === CQAppConstant.PUSH_PORTAL_UI.PUSH_REQUEST_STATUSES.SCHEDULED && canSchedule) && (
                                        <Button
                                            variant="icon"
                                            title={t('push_portal_edit_scheduled_time')}
                                            className="slds-m-left_xx-small"
                                            onClick={() => {
                                                const currentVal = selectedScheduledRequest.scheduledTime || selectedScheduledRequest.createdAt;
                                                setEditScheduledDateTime(formatDateOnlyInput(currentVal));
                                                setEditScheduledError('');
                                                setIsEditingScheduledTime(true);
                                            }}
                                        >
                                            <Icon category="utility" name="edit" size="x-small" />
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <div className="slds-grid slds-grid_vertical-align-center">
                                        <input
                                            id="edit-scheduled-time-input"
                                            aria-labelledby="view-scheduled-time-label"
                                            type="date"
                                            className="slds-input slds-m-right_x-small slds-grow"
                                            min={getMinSelectableUtcDate()}
                                            value={editScheduledDateTime}
                                            onChange={(e) => {
                                                setEditScheduledDateTime(e.target.value);
                                                setEditScheduledError('');
                                            }}
                                            required
                                        />
                                        <Button
                                            label={t('push_portal_save')}
                                            variant="brand"
                                            className="slds-button_small"
                                            onClick={() => handleUpdateScheduledTime()}
                                        />
                                        <Button
                                            label={t('push_portal_cancel')}
                                            variant="neutral"
                                            className="slds-button_small slds-m-left_xx-small"
                                            onClick={() => {
                                                setIsEditingScheduledTime(false);
                                                setEditScheduledError('');
                                            }}
                                        />
                                    </div>
                                    {editScheduledError && (
                                        <div className="slds-text-color_error slds-text-body_small slds-m-top_xx-small">
                                            {editScheduledError}
                                        </div>
                                    )}
                                    <div className="slds-box slds-box_x-small slds-theme_shade slds-media slds-media_center slds-m-top_small">
                                        <div className="slds-media__figure">
                                            <Icon category="utility" name="clock" size="x-small" className="slds-icon-text-warning" />
                                        </div>
                                        <div className="slds-media__body">
                                            <p className="slds-text-body_small">
                                                {t('push_portal_scheduler_daily_note', { time: CQAppConstant.PUSH_PORTAL_UI.SCHEDULER_RUN_UTC.DISPLAY })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }
                cancelLabel={t('push_portal_close')}
                onCancel={() => {
                    setSelectedScheduledRequest(null);
                    setIsEditingScheduledTime(false);
                    setEditScheduledError('');
                }}
                supportingDetails={
                    selectedScheduledRequest && (() => {
                        const orgs = selectedScheduledRequest.orgs;
                        if (!orgs || orgs.length === 0) {
                            return (
                                <div className="slds-text-body_small slds-text-color_weak">
                                    {t('push_portal_no_org_details')}
                                </div>
                            );
                        }
                        return (
                            <div className="slds-m-top_medium">
                                <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-bottom_x-small">
                                    <div className="slds-text-title_caps">{t('push_portal_orgs')}</div>
                                    {selectedScheduledRequest?.status === CQAppConstant.PUSH_PORTAL_UI.PUSH_REQUEST_STATUSES.SCHEDULED && (
                                        <Button
                                            label={t('push_portal_cancel_all')}
                                            variant="text-destructive"
                                            onClick={() => handleCancelClick()}
                                        />
                                    )}
                                </div>
                                {orgs.map(o => (
                                    <div key={o.orgId} className="slds-box slds-box_x-small slds-theme_shade slds-grid slds-grid_align-spread slds-grid_vertical-align-center slds-m-bottom_x-small">
                                        <div className="slds-truncate" title={o.orgId}>
                                            <div className="slds-truncate">
                                                {t('push_portal_org_id')} - {o.orgId}
                                            </div>
                                            <div className="slds-truncate slds-text-body_small slds-text-color_weak">
                                                {t('push_portal_org_name')} - {o.orgName || '—'}
                                            </div>
                                        </div>
                                        {selectedScheduledRequest?.status === CQAppConstant.PUSH_PORTAL_UI.PUSH_REQUEST_STATUSES.SCHEDULED && (
                                            <Button
                                                label={t('push_portal_cancel')}
                                                variant="destructive"
                                                className="slds-button_small slds-m-left_x-small"
                                                onClick={() => handleCancelClick(o.orgId)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })()
                }
            />
            <CQConfirmationModal
                isOpen={editScheduledConfirmOpen}
                heading={t('push_portal_edit_scheduled_time_title')}
                message={t('push_portal_edit_scheduled_time_confirm', { scheduledTime: formatScheduledDateDisplay(buildScheduledTimeIso(editScheduledDateTime)) })}
                confirmLabel={t('push_portal_confirm')}
                cancelLabel={t('push_portal_close')}
                confirmActionStyle="brand"
                isActionInProgress={editScheduledLoading}
                progressLabel={t('push_portal_saving')}
                onConfirm={() => handleUpdateScheduledTime(true)}
                onCancel={() => setEditScheduledConfirmOpen(false)}
                size={CQAppConstant.MODAL_SIZE.COMPACT}
            />
            <CQConfirmationModal
                isOpen={cancelConfirmOpen}
                heading={t('push_portal_cancel_scheduled_upgrade')}
                message={
                    isCancelAll ? (
                        <div className="slds-text-align_center">{t('push_portal_cancel_all_confirmation')}</div>
                    ) : (
                        <div className="slds-text-align_center">
                            {t('push_portal_cancel_org_confirmation_prefix')}{' '}
                            <strong>{orgIdsToCancel[0]}</strong>?
                        </div>
                    )
                }
                confirmLabel={t('push_portal_confirm')}
                cancelLabel={t('push_portal_close')}
                confirmActionStyle="destructive"
                promptStyle="error"
                isActionInProgress={cancelLoading}
                progressLabel={t('push_portal_cancelling')}
                onConfirm={() => handleCancelScheduledRequest(isCancelAll ? undefined : orgIdsToCancel)}
                onCancel={() => { setCancelConfirmOpen(false); setOrgIdsToCancel([]); setIsCancelAll(false); }}
                size={CQAppConstant.MODAL_SIZE.COMPACT}
            />
            {toastOpen && (
                <div className='slds-notify_container slds-is-fixed'>
                    <Toast
                        labels={{ heading: toastMessage }}
                        variant={toastVariant}
                        onRequestClose={() => setToastOpen(false)}
                        duration={6000}
                    />
                </div>
            )}
            <CQConfirmationModal
                isOpen={abortConfirmOpen}
                heading={t('push_portal_abort_confirm_title')}
                message={t('push_portal_abort_confirm_body', {
                    orgName: itemToAbort?.orgName || CQAppConstant.UNKNOWN_ORG,
                    orgId: itemToAbort?.orgId || ''
                })}
                confirmLabel={t('push_portal_abort')}
                cancelLabel={t('push_portal_cancel')}
                promptStyle='warning'
                isActionInProgress={abortLoading}
                progressLabel={t('push_portal_aborting')}
                supportingDetails={<div>{t('push_portal_abort_irreversible_warning')}</div>}
                onConfirm={handleAbortConfirm}
                onCancel={() => {
                    setAbortConfirmOpen(false);
                    setItemToAbort(null);
                }}
            />
        </IconSettings>
    );
}

export default CQPushPortal;
